import { Badge, Group, HoverCard, List, Stack, Table, Text, Tooltip } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { SIGNAL_SPECS, type TrustSignal, type TrustVerdict } from '@prooffleet/contracts';
import { api } from '@prooffleet/client';
import { verdictColor } from '@prooffleet/ui';
import { EmptyState, PageHeader, TableSkeleton } from '../components/Page.js';

interface FeedItem {
  _id: string;
  unitId: string;
  unitNumber: string;
  verdict: string;
  counts: { ok: number; warn: number; critical: number; skipped: number };
  durationSeconds: number;
  submittedAt: { serverAt: string; skewMs: number; suspect: boolean };
  templateKey: string;
}

interface TrustRow {
  score: number;
  verdict: TrustVerdict;
  signals?: { signal: TrustSignal; detail: string }[];
}

const TRUST_LABEL: Record<TrustVerdict, string> = {
  trusted: 'Trusted',
  review: 'Review',
  suspect: 'Suspect',
};

const TRUST_COLOR: Record<TrustVerdict, string> = {
  // Доверенный осмотр — норма, а не достижение: нейтральный серый.
  // Акцентный цвет на девяноста пяти строках из ста перестаёт быть акцентом.
  trusted: 'gray',
  review: 'orange',
  suspect: 'red',
};

const VERDICT_LABEL: Record<string, string> = {
  pass: 'Pass',
  defects: 'Defects',
  critical: 'Critical',
};

function stripeClass(verdict: string): string {
  if (verdict === 'critical') return 'pf-stripe pf-stripe-critical';
  if (verdict === 'defects') return 'pf-stripe pf-stripe-warn';
  return 'pf-stripe pf-stripe-ok';
}

/**
 * Оценка достоверности — всегда с причинами.
 *
 * Число без объяснения читается как обвинение; с объяснением это повод
 * задать вопрос. Водителю эта колонка недоступна по построению: у роли
 * `driver` нет права `trust:read` (правило 17).
 */
function TrustCell({ row }: { row: TrustRow | undefined }): React.JSX.Element {
  if (row === undefined) return <Text c="dimmed">—</Text>;

  const badge = (
    <Badge color={TRUST_COLOR[row.verdict]} className="pf-num">
      {row.score} · {TRUST_LABEL[row.verdict]}
    </Badge>
  );

  if (row.signals === undefined || row.signals.length === 0) return badge;

  return (
    <HoverCard width={340} shadow="md" withArrow>
      <HoverCard.Target>
        <div style={{ cursor: 'help' }}>{badge}</div>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <Text fw={600} fz="sm" mb={6}>
          Why the score dropped
        </Text>
        <List spacing={6} size="sm">
          {row.signals.map((s) => (
            <List.Item key={s.signal}>
              <Text fz="sm" fw={500}>
                {SIGNAL_SPECS[s.signal].label}
              </Text>
              <Text fz="xs" c="dimmed">
                {SIGNAL_SPECS[s.signal].explanation}
              </Text>
              <Text fz="xs">{s.detail}</Text>
            </List.Item>
          ))}
        </List>
      </HoverCard.Dropdown>
    </HoverCard>
  );
}

/** Лента осмотров. Состояние читается полосой слева до чтения текста. */
export function Feed(): React.JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['inspections'],
    queryFn: () => api<{ inspections: FeedItem[] }>('/inspections?limit=100'),
    refetchInterval: 30_000,
  });

  const items = data?.inspections ?? [];

  // Оценка достоверности грузится отдельно: у ролей без права `trust:read`
  // этот запрос вернёт 403, и лента должна работать без него.
  const { data: trust } = useQuery({
    queryKey: ['trust', items.map((i) => i._id)],
    enabled: items.length > 0,
    retry: false,
    queryFn: () =>
      api<{ assessments: Record<string, TrustRow> }>('/trust/batch', {
        method: 'POST',
        body: { inspectionIds: items.map((i) => i._id) },
      }),
  });

  return (
    <Stack gap={0}>
      <PageHeader
        title="Inspections"
        count={isLoading ? undefined : items.length}
        hint="Newest first. The stripe on the left carries the result, so a critical finding reads before the text does."
      />

      {isLoading && <TableSkeleton rows={7} cols={6} />}

      {!isLoading && items.length === 0 && (
        <EmptyState
          title="No inspections in this period"
          hint="Records appear here as drivers submit them from the phone."
        />
      )}

      {!isLoading && items.length > 0 && (
      <Table.ScrollContainer minWidth={860}>
        <Table className="pf-table">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Unit</Table.Th>
              <Table.Th>Result</Table.Th>
              <Table.Th>Defects</Table.Th>
              <Table.Th>Duration</Table.Th>
              <Table.Th>Trust</Table.Th>
              <Table.Th>Submitted</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((item) => (
              <Table.Tr key={item._id} className={stripeClass(item.verdict)}>
                <Table.Td>
                  <Link to={`/units/${item.unitId}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                    <Text fw={600} fz="sm" lh={1.3}>
                      {item.unitNumber}
                    </Text>
                  </Link>
                  <Text className="pf-mono">{item.templateKey}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge color={verdictColor(item.verdict)}>
                    {VERDICT_LABEL[item.verdict] ?? item.verdict}
                  </Badge>
                </Table.Td>
                <Table.Td className="pf-num">
                  {item.counts.critical > 0 && (
                    <Badge color="red" mr={4}>
                      {item.counts.critical}
                    </Badge>
                  )}
                  {item.counts.warn > 0 && <Badge color="orange">{item.counts.warn}</Badge>}
                  {item.counts.critical === 0 && item.counts.warn === 0 && (
                    <Text c="dimmed">—</Text>
                  )}
                </Table.Td>
                <Table.Td className="pf-num">
                  <Text fz="sm" span>
                    {Math.round(item.durationSeconds / 60)}
                  </Text>
                  <Text fz="xs" c="dimmed" span>
                    {' '}
                    min
                  </Text>
                </Table.Td>
                <Table.Td>
                  <TrustCell row={trust?.assessments[item._id]} />
                </Table.Td>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <Text fz="sm">{new Date(item.submittedAt.serverAt).toLocaleString()}</Text>
                    {item.submittedAt.suspect && (
                      // Расхождение часов само по себе не подделка,
                      // но оно должно быть видно менеджеру.
                      <Tooltip label="Device clock differs from the server">
                        <Badge color="orange" size="sm">
                          clock
                        </Badge>
                      </Tooltip>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      )}
    </Stack>
  );
}
